import React, { useState } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Button } from 'src/components/ui/button';
import DataTable from 'src/components/ui/data-table';
import { Plus, Pencil, Store } from 'lucide-react';
import { StatusBadge } from 'src/components/common/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useVendors } from 'src/services/api/queries/vendorQueries';
import {
  useCreateVendor,
  useUpdateVendor,
} from 'src/services/api/mutations/vendorMutations';
import { useToast } from 'src/hooks/use-toast';
import { VendorModal } from 'src/components/Modals/VendorModal';

const StatusCell = ({ isActive }) => (
  <StatusBadge value={isActive ? 'ACTIVE' : 'INACTIVE'} />
);

const ActionsCell = ({ row, onEdit }) => (
  <div className="flex items-center gap-1">
    <Button
      variant="ghost"
      size="icon"
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-md hover:bg-muted"
      title="Edit vendor"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(row);
      }}
    >
      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
    </Button>
  </div>
);

const createColumns = (handleEditClick) => [
  {
    key: 'name',
    header: 'Vendor Name',
    minWidth: '220px',
    render: (v) => (
      <span className="text-[13px] font-[550] text-foreground-strong">{v}</span>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    minWidth: '200px',
    render: (v) => (
      <div className="min-w-0 w-full">
        <span
          className="block text-[12.5px] text-muted-foreground truncate"
          title={v}
        >
          {v || <span className="italic text-muted-foreground">—</span>}
        </span>
      </div>
    ),
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (v) => <StatusCell isActive={v} />,
  },
  {
    key: 'actions',
    header: '',
    width: '60px',
    minWidth: '60px',
    render: (_v, row) => <ActionsCell row={row} onEdit={handleEditClick} />,
  },
];

function Vendors() {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { data, isLoading, refetch } = useVendors(false);
  const vendors = data?.data ?? data ?? [];

  const createVendorMutation = useCreateVendor();
  const updateVendorMutation = useUpdateVendor();

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (row) => {
    setEditingItem(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateVendorMutation.mutateAsync({
          id: editingItem.id,
          data: formData,
        });
        toast({
          title: 'Success',
          description: 'Vendor updated successfully.',
          variant: 'success',
        });
      } else {
        await createVendorMutation.mutateAsync(formData);
        toast({
          title: 'Success',
          description: 'Vendor created successfully.',
          variant: 'success',
        });
      }
      handleModalClose();
      refetch();
    } catch {
      toast({
        title: 'Error',
        description: `Failed to ${editingItem ? 'update' : 'create'} vendor.`,
        variant: 'destructive',
      });
    }
  };

  const columns = createColumns(handleEditClick);

  const emptyState = (
    <div className="bg-card rounded-xl border border-border flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
        <Store className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
        No vendors found
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs mb-5 leading-relaxed">
        Get started by adding your first vendor.
      </p>
      <button
        type="button"
        onClick={handleCreate}
        className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-background text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
        Add Vendor
      </button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Vendors"
        actions={
          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-[border-color,color,background-color] cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            Add Vendor
          </button>
        }
      />

      <PageContainer className="space-y-5">
        <DataTable
          data={vendors}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          className="rounded-xl overflow-hidden"
          emptyState={emptyState}
          rowClassName={() =>
            'hover:bg-brand-bg-subtle transition-colors cursor-pointer'
          }
        />
      </PageContainer>

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Vendor' : 'Add Vendor'}
            </DialogTitle>
            <DialogDescription>
              {editingItem
                ? `Editing: ${editingItem.name}`
                : 'Add a new vendor to the platform.'}
            </DialogDescription>
          </DialogHeader>
          <VendorModal
            initialData={editingItem}
            onSubmit={handleSubmit}
            onCancel={handleModalClose}
            loading={
              createVendorMutation.isPending || updateVendorMutation.isPending
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Vendors;
